const ACTIVE_SUBSCRIPTION_STATUSES = new Set(["active", "trialing"]);
const TERMINAL_PURCHASE_STATUSES = new Set(["refunded", "revoked"]);

export const commercialOfferCatalog = Object.freeze({
  pro_lifetime: Object.freeze({
    key: "pro_lifetime",
    billing: "one_time",
    grantsTier: "pro",
    unitAmountCents: 4_900,
    currency: "usd",
  }),
  pro_plus_monthly: Object.freeze({
    key: "pro_plus_monthly",
    billing: "subscription",
    interval: "month",
    grantsTier: "pro_plus",
    unitAmountCents: 1_200,
    currency: "usd",
  }),
  pro_plus_annual: Object.freeze({
    key: "pro_plus_annual",
    billing: "subscription",
    interval: "year",
    grantsTier: "pro_plus",
    unitAmountCents: 9_900,
    currency: "usd",
  }),
  storage_10gb: Object.freeze({
    key: "storage_10gb",
    billing: "subscription_addon",
    interval: "month",
    requiresTier: "pro_plus",
    storageBytes: 10_000_000_000,
    unitAmountCents: 300,
    currency: "usd",
  }),
  storage_50gb: Object.freeze({
    key: "storage_50gb",
    billing: "subscription_addon",
    interval: "month",
    requiresTier: "pro_plus",
    storageBytes: 50_000_000_000,
    unitAmountCents: 800,
    currency: "usd",
  }),
  ai_100: Object.freeze({
    key: "ai_100",
    billing: "consumable",
    requiresTier: "pro_plus",
    aiCredits: 100,
    unitAmountCents: 500,
    currency: "usd",
  }),
  ai_350: Object.freeze({
    key: "ai_350",
    billing: "consumable",
    requiresTier: "pro_plus",
    aiCredits: 350,
    unitAmountCents: 1_500,
    currency: "usd",
  }),
});

export function validateCommercialConfiguration(configuration) {
  const errors = [];
  const priceIds = new Set();
  for (const offer of Object.values(commercialOfferCatalog)) {
    const priceId = configuration?.priceIds?.[offer.key];
    if (typeof priceId !== "string" || !/^price_[A-Za-z0-9]+$/.test(priceId)) {
      errors.push(`Missing or invalid provider price for ${offer.key}`);
      continue;
    }
    if (priceIds.has(priceId)) errors.push(`Provider price is reused: ${priceId}`);
    priceIds.add(priceId);
  }
  if (typeof configuration?.webhookSecret !== "string" || !configuration.webhookSecret.startsWith("whsec_")) {
    errors.push("Missing or invalid webhook signing secret");
  }
  return { ready: errors.length === 0, errors };
}

export function resolveCommercialOffer(offerKey, currentTier = "free") {
  const offer = commercialOfferCatalog[offerKey];
  if (!offer) return { allowed: false, reason: "unknown_offer" };
  if (offer.requiresTier && offer.requiresTier !== currentTier) {
    return { allowed: false, reason: "tier_required", requiredTier: offer.requiresTier };
  }
  return { allowed: true, offer };
}

export function normalizeProviderEntitlement(input) {
  const tier = ["free", "pro", "pro_plus_beta", "pro_plus"].includes(input?.tier) ? input.tier : "free";
  const status = ["active", "trialing", "past_due", "canceled", "expired", "refunded", "revoked"].includes(input?.status)
    ? input.status
    : "expired";
  const effectiveAt = Math.max(0, Math.trunc(Number(input?.effectiveAt) || 0));
  const periodEndsAt = input?.periodEndsAt == null ? null : Math.max(0, Math.trunc(Number(input.periodEndsAt) || 0));
  return { tier, status, effectiveAt, periodEndsAt };
}

export function shouldApplyProviderEntitlement(current, incoming, now = Date.now()) {
  const existing = normalizeProviderEntitlement(current);
  const candidate = normalizeProviderEntitlement(incoming);
  if (!candidate.effectiveAt || candidate.effectiveAt > now + 5 * 60_000) return false;
  if (candidate.effectiveAt < existing.effectiveAt) return false;
  if (candidate.effectiveAt === existing.effectiveAt
    && TERMINAL_PURCHASE_STATUSES.has(existing.status)
    && !TERMINAL_PURCHASE_STATUSES.has(candidate.status)) return false;
  return true;
}

export function entitlementAccess(entitlement, now = Date.now()) {
  const normalized = normalizeProviderEntitlement(entitlement);
  const withinPeriod = normalized.periodEndsAt == null || normalized.periodEndsAt > now;
  const paidAccess = ACTIVE_SUBSCRIPTION_STATUSES.has(normalized.status) && withinPeriod;
  const ownsPro = normalized.tier === "pro" && normalized.status === "active";
  return {
    tier: paidAccess || ownsPro ? normalized.tier : "free",
    localPro: ownsPro || (paidAccess && normalized.tier === "pro_plus"),
    hosted: paidAccess && ["pro_plus", "pro_plus_beta"].includes(normalized.tier),
  };
}

export function verifyProviderWebhookEnvelope({ eventId, createdAt, payloadBytes, signatureVerified }) {
  if (!signatureVerified) return { accepted: false, reason: "signature" };
  if (typeof eventId !== "string" || !/^evt_[A-Za-z0-9]+$/.test(eventId)) return { accepted: false, reason: "event_id" };
  if (!Number.isSafeInteger(createdAt) || createdAt <= 0) return { accepted: false, reason: "created_at" };
  if (!Number.isSafeInteger(payloadBytes) || payloadBytes < 2 || payloadBytes > 256_000) return { accepted: false, reason: "payload_size" };
  return { accepted: true };
}
